import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Button } from '@predoc/cow';
import {
  ArrowLeft,
  ArrowUpToLine,
  Building2,
  Download,
  UserRoundPlus,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { PDFViewer } from '@/components';
import { Permission, useHasPermission } from '@/utils/roles';
import { downloadFromUrl, sanitizeFilename } from '@/utils/downloadUtils';
import { useDocumentDownload } from './useDocumentDownload';
import {
  type DocumentEntry,
  type DocumentKey,
  type DocumentViewerPatient,
} from './types';

export interface DocumentViewerShellProps {
  onClose: () => void;
  isLoading: boolean;
  isError?: boolean;
  isSuccess?: boolean;
  entry?: DocumentEntry;
  patient?: DocumentViewerPatient;
  documentKey?: DocumentKey;
  patientId?: string;
  errorMessage?: string;
  missingFileMessage?: string;
  highlightAnchorId?: string;
  headerActions?: ReactNode;
  sidebar?: ReactNode;
  loadingSlot?: ReactNode;
}

const DEFAULT_ERROR_MESSAGE = 'Unable to load record';
const DEFAULT_MISSING_FILE_MESSAGE = 'This record has no viewable file';

const formatUploadDate = (source: string | Date | undefined): string => {
  if (!source) return '';
  const date = typeof source === 'string' ? new Date(source) : source;
  if (Number.isNaN(date.getTime())) return '';
  return format(date, 'MM/dd/yyyy');
};

const DefaultLoadingSlot = () => (
  <div className="flex h-full items-center justify-center">
    <div
      role="status"
      aria-label="Loading"
      className="size-10 animate-spin rounded-full border-4 border-gray-200 border-t-gray-600"
    />
  </div>
);

export const DocumentViewerShell = ({
  onClose,
  isLoading,
  isError = false,
  isSuccess = false,
  entry,
  patient,
  documentKey,
  patientId,
  errorMessage = DEFAULT_ERROR_MESSAGE,
  missingFileMessage = DEFAULT_MISSING_FILE_MESSAGE,
  highlightAnchorId,
  headerActions,
  sidebar,
  loadingSlot,
}: DocumentViewerShellProps) => {
  const templateScrollRef = useRef<HTMLDivElement>(null);
  const { hasPermission: canExport } = useHasPermission(
    Permission.EXPORT_MEDICAL_RECORDS,
  );

  const patientFullName = patient
    ? [patient.firstName, patient.middleName, patient.lastName]
        .filter(Boolean)
        .join(' ')
    : '';
  const profileName = patient?.organizationProfile?.displayName;
  const title = entry?.title ?? '';
  const uploadDate = entry ? formatUploadDate(entry.uploadedAt) : '';
  const missingPdfUrl = entry?.kind === 'pdf' && !entry.signedUrl;

  const { disabled: downloadDisabled, download } = useDocumentDownload({
    documentKey,
    entryKind: entry?.kind,
    sourceRequestId: entry?.sourceRequestId,
    medicationDataType:
      entry?.kind === 'template' ? entry.medicationDataType : undefined,
    patientId,
    patient,
  });

  const handleDownload = useCallback(() => {
    if (entry?.kind === 'pdf' && entry.signedUrl) {
      const filename = sanitizeFilename(`${title}-${patient?.key ?? ''}`);
      downloadFromUrl(entry.signedUrl, `${filename}.pdf`);
    } else {
      download();
    }
  }, [entry, title, patient, download]);

  const effectiveDownloadDisabled =
    entry?.kind === 'pdf' ? isLoading || !entry.signedUrl : downloadDisabled;
  const canDownloadDocument = !!documentKey;

  useEffect(() => {
    if (isError) {
      toast.error(errorMessage);
      onClose();
      return;
    }
    if (!isLoading && isSuccess) {
      if (!entry) {
        toast.error(errorMessage);
        onClose();
        return;
      }
      if (missingPdfUrl) {
        toast.error(missingFileMessage);
        onClose();
      }
    }
  }, [
    isError,
    isLoading,
    isSuccess,
    entry,
    missingPdfUrl,
    errorMessage,
    missingFileMessage,
    onClose,
  ]);

  useEffect(() => {
    if (isLoading || entry?.kind !== 'template' || !highlightAnchorId) return;

    const container = templateScrollRef.current;
    if (!container) return;

    const target = Array.from(
      container.querySelectorAll<HTMLElement>('[id]'),
    ).find((element) => element.id === highlightAnchorId);

    if (!target) return;

    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    target.focus({ preventScroll: true });
  }, [entry, highlightAnchorId, isLoading]);

  const renderBody = () => {
    if (isLoading) {
      return loadingSlot ?? <DefaultLoadingSlot />;
    }
    if (!entry || missingPdfUrl) {
      return null;
    }
    if (entry.kind === 'pdf' && entry.signedUrl) {
      return <PDFViewer file={entry.signedUrl} className="h-full" />;
    }
    if (entry.kind === 'template') {
      return (
        <div className="relative h-full">
          <div
            ref={templateScrollRef}
            className="h-full overflow-auto bg-neutral-200 px-9 py-8"
          >
            {entry.render({ patient })}
          </div>
          <Button
            variant="default"
            size="icon"
            onClick={() =>
              templateScrollRef.current?.scrollTo({
                top: 0,
                behavior: 'smooth',
              })
            }
            className="absolute right-4 bottom-4 z-10 size-12 rounded-full bg-neutral-900 shadow-lg hover:bg-neutral-800"
            aria-label="Scroll to top"
          >
            <ArrowUpToLine className="size-5" />
          </Button>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <PageHeader>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close document viewer"
            className="size-9"
          >
            <ArrowLeft className="size-5" />
          </Button>
          {title ? (
            <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
          ) : null}
        </div>

        <div className="flex items-center gap-6">
          {uploadDate && (
            <span className="flex items-center gap-1 text-sm text-gray-600">
              Uploaded {uploadDate}
            </span>
          )}
          {patientFullName && (
            <span className="flex items-center gap-1.5 text-sm text-gray-900">
              <UserRoundPlus className="size-4" />
              {patientFullName}
            </span>
          )}
          {profileName && (
            <span className="flex items-center gap-1.5 text-sm text-gray-900">
              <Building2 className="size-4 text-blue-400" />
              {profileName}
            </span>
          )}
          {headerActions}
          {canExport && canDownloadDocument ? (
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={handleDownload}
              disabled={effectiveDownloadDisabled}
              aria-label="Download"
            >
              <Download className="size-4" />
              Download
            </Button>
          ) : null}
        </div>
      </PageHeader>

      <div className="flex flex-1 overflow-hidden">
        {sidebar}
        <div className="flex-1 overflow-hidden">{renderBody()}</div>
      </div>
    </>
  );
};
